import 'react-international-phone/style.css';
import React from 'react'
import ApiCustomer from '../code/api/ApiCustomer';
import Customer from '../code/models/Customer';
import CustomerDetail from '../code/models/CustomerDetail';
import { stringToDate } from '../code/helpers/stringToDate';
import CustomerChangeTracker from '../code/helpers/CustomerChangeTracker';
import ButtonConfirm, { ButtonConfirmSize } from './ButtonConfirm';
import Api_Config from '../code/api/ApiConfig';
import { formatCurrency } from '../code/helpers/numberFormatting';
import { CountrySelector, ParsedCountry, PhoneInput } from "react-international-phone";
import '../css/customerinfodetail.css';
import AuthAdminUserModel from "../code/models/AuthAdminUserModel";
import Toaster from "./Toaster";
import { ms } from "date-fns/locale";

interface ICustomerInfoDetail {
    customer: Customer;
    currentUser: AuthAdminUserModel;
    customerDeleted: Function;
}

export default function CustomerInfoDetail({ customer, currentUser, customerDeleted }: ICustomerInfoDetail) {
    const [customerDetail, setCustomerDetail] = React.useState<CustomerDetail | null>(null);
    const [editCustomerDetail, setEditCustomerDetail] = React.useState<CustomerDetail | null>(null);
    const [anyChanges, setAnyChanges] = React.useState<boolean>(false);
    const [validForm, setValidForm] = React.useState<boolean>(true);
    const [validUser, setValidUser] = React.useState<boolean>(true);
    const [toastMessage, setToastMessage] = React.useState<string>("");
    const [resetId, setResetId] = React.useState<string>("123456789");
    const company = Api_Config.getSelectedCompany();

    React.useEffect(() => {
        loadCustomerInfo();
    }, []);

    async function loadCustomerInfo() {
        ApiCustomer.getCustomer(customer.customerId).then(data => {
            setCustomerDetail(data);
            setEditCustomerDetail(structuredClone(data));
        });
    }

    function getDeactivatedInfo() {
        return (
            <React.Fragment>
                {customerDetail?.deactivated &&
                    <div className="row text-bg-danger p-3">
                        <span className="col-md-3">Customer is Deactivated</span>
                        <span className="col-md-3">Deactivated Date: {stringToDate(customerDetail?.deactivatedDate || "")}</span>
                        <span className="col-md-5 pad-left-5">Reason: {customerDetail?.deactivatedReason || ""}</span>
                    </div>}
                {customerDetail?.deactivated && customerDetail?.customerBlocked &&
                    <div className="row text-bg-danger p-3">
                        <span className="col-md-3">Customer Blocked</span>
                        <span className="col-md-8">Reason: {customerDetail?.customerBlockReason || ""}</span>
                    </div>}
            </React.Fragment>
        )
    }

    function buildBalanceDue() {
        return (
            <React.Fragment>
                {customerDetail != null && customerDetail.balanceDue > 0 &&
                    <span className="balance-due col-md-12">Balance Due: {formatCurrency(customerDetail.balanceDue, company.currency)}</span>}
            </React.Fragment>
        )
    }

    function showDeactivatedToast() {
        setToastMessage("The customer is now deactivated.");
    }

    function showReactivatedToast() {
        setToastMessage("The customer is now reactivated.");
    }

    function showPasswordUpdatedToast() {
        setToastMessage("The customer password is now set to 'changeme'.");
    }

    function showUnableToast() {
        setToastMessage("Unabe to update customer.");
    }

    function handleDeactivateConfirm(doDeactivate: boolean) {
        const msg = "Customer requested to deactivate.";
        if (doDeactivate && customer) {
            ApiCustomer.deactivateCustomer(customer.customerId, msg).then(result => {
                if (result) {
                    showDeactivatedToast();
                    loadCustomerInfo();
                    customer.deactivated = true;
                    //CustomerChangeTracker.trigger(customer.customerId);
                } else {
                    showUnableToast();
                }
            });
        }
    }

    function handleActivateConfirm(doActivate: boolean) {
        if (doActivate && customer) {
            ApiCustomer.activateCustomer(customer.customerId, true).then(result => {
                if (result) {
                    showReactivatedToast();
                    loadCustomerInfo();
                    customer.deactivated = false;
                    //CustomerChangeTracker.trigger(customer.customerId);
                } else {
                    showUnableToast();
                }
            });
        }
    }

    function handleResetPasswordConfirm(doReset: boolean) {
        if (doReset && customer) {
            ApiCustomer.resetPassword(customer.customerId).then(result => {
                if (result) {
                    showPasswordUpdatedToast();
                } else {
                    showUnableToast();
                }
            });
        }
    }

    function handleDeleteCustomer(confirm: boolean) {
        if (!confirm) return;
        ApiCustomer.deleteCustomer(customer.customerId).then((success) => {
            if (success) {
                setToastMessage("Customer has been deleted.");
                setTimeout(customerDeleted, 3000);
                customerDeleted();
            }
        });
    }

    function buildDeleteCustomerButton(): React.ReactNode {
        if (anyChanges || !currentUser.canDeleteCustomer) { return; }
        return (
            <ButtonConfirm enabled={!!customerDetail} buttonText="Delete Customer" confirmTitle="Delete Customer"
                confirmMessage={`Are you sure you want to delete customer ${customer.name}?  This cannot be undone.`}
                cssClass="btn-outline-secondary col-md-2" buttonSize={ButtonConfirmSize.Small} onConfirm={handleDeleteCustomer} />
        )
    }

    function validateForm(): boolean {
        if (!editCustomerDetail) return false;
        let isValidForm =
            validUser &&
            !!editCustomerDetail.userId &&
            !!editCustomerDetail.firstName &&
            !!editCustomerDetail.lastName &&
            !!editCustomerDetail.email &&
            !!editCustomerDetail.companyEmployeeId;

        setValidForm(isValidForm);

        return isValidForm;
    }

    function checkHasChanges() {
        if (!editCustomerDetail || !customerDetail) return false;
        let hasChanges =
            editCustomerDetail.userId !== customerDetail.userId ||
            editCustomerDetail.firstName !== customerDetail.firstName ||
            editCustomerDetail.nickname !== customerDetail.nickname ||
            editCustomerDetail.lastName !== customerDetail.lastName ||
            editCustomerDetail.email !== customerDetail.email ||
            editCustomerDetail.phoneCountryCode !== customerDetail.phoneCountryCode ||
            !IsPhoneSame(editCustomerDetail.phone || "", customerDetail.phone || "") ||
            editCustomerDetail.companyEmployeeId !== customerDetail.companyEmployeeId ||
            editCustomerDetail.address1 !== customerDetail.address1 ||
            editCustomerDetail.address2 !== customerDetail.address2 ||
            editCustomerDetail.city !== customerDetail.city ||
            editCustomerDetail.state !== customerDetail.state ||
            editCustomerDetail.zipcode !== customerDetail.zipcode ||
            editCustomerDetail.country !== customerDetail.country;

        if (editCustomerDetail.userId !== customerDetail.userId) {
            doValidateUserId();
        }
        validateForm();
        setAnyChanges(hasChanges);
    }

    const regex = /([()-\s\+])/gi;
    function cleanPhone(phone: string) {
        return phone.replaceAll(regex, "");
    }

    function IsPhoneSame(phone: string, phone2: string) {
        return cleanPhone(phone) === cleanPhone(phone2);
    }

    function resetView() {
        var myEpoch = new Date().getTime() / 1000.0;
        setResetId(`${myEpoch}`);
    }

    function cancelChangesClick(): void {
        setEditCustomerDetail(structuredClone(customerDetail));
        setAnyChanges(false);
        setValidForm(true);
        setValidUser(true);
        resetView();
    }

    function saveChangesClick() {
        if (!editCustomerDetail || !customerDetail) return false;
        if (anyChanges) {
            ApiCustomer.updateCustomer(editCustomerDetail).then(data => {
                CustomerChangeTracker.trigger(customer.customerId);
                setCustomerDetail(data);
                setEditCustomerDetail(structuredClone(data));
                setAnyChanges(false);
                setValidForm(true);
                setValidUser(true);
                resetView();
            });
        }
    }


    function setCountryInfo(country: ParsedCountry) {
        if (!editCustomerDetail) return;
        editCustomerDetail.country = country.iso2;
        checkHasChanges();
    }

    function buildActivateDeactivateButtons(): React.ReactNode {
        return (
            <React.Fragment key={`actbtns${anyChanges}${customerDetail?.deactivated}`}>
                {!anyChanges && !customerDetail?.deactivated && <ButtonConfirm enabled={!!customer} buttonText="Deactivate" confirmTitle="Deactivate"
                    confirmMessage={`Deactivate ${customer.name} for bidding?`} buttonSize={ButtonConfirmSize.Small} cssClass="btn btn-outline-danger col-md-2"
                    onConfirm={handleDeactivateConfirm} />}
                {!anyChanges && customerDetail?.deactivated && <ButtonConfirm enabled={!!customer} buttonText="Reactivate" confirmTitle="Reactivate"
                    confirmMessage={`Reactivate ${customer.name} for bidding?`} buttonSize={ButtonConfirmSize.Small} cssClass="btn btn-outline-danger col-md-2"
                    onConfirm={handleActivateConfirm} />}
            </React.Fragment>
        )
    }

    function doValidateUserId() {
        if (!editCustomerDetail?.userId) {
            setValidUser(false);
        } else if (editCustomerDetail.userId.toLowerCase() === customerDetail?.userId.toLowerCase()) {
            setValidUser(true);
        } else {
            ApiCustomer.validateUserId(editCustomerDetail.userId).then((data) => {
                setValidUser(data);
                if (!data) {
                    setToastMessage("User Id is invalid.")
                }
            });
        }
    }

    function setPhoneValues(phone: string, meta: { country: ParsedCountry; inputValue: string; }): void {
        if (!editCustomerDetail) return;

        if (Api_Config.isLegacy() && !Api_Config.isUk()) {
            editCustomerDetail.phone = cleanPhone(phone);
            editCustomerDetail.phoneCountryCode = "";
        } else {
            let justThePhone = phone.replace(`+${meta.country.dialCode}`, "");
            editCustomerDetail.phone = justThePhone;
            editCustomerDetail.phoneCountryCode = meta.country.dialCode;
        }
        checkHasChanges();
    }

    function getPhoneNumber(customerDetail: CustomerDetail | null): string {
        if (!customerDetail) return "";

        if (Api_Config.isLegacy() && !Api_Config.isUk()) {
            return cleanPhone(customerDetail.phone || "");
        } else {
            return cleanPhone(`${customerDetail?.phoneCountryCode || ""}${customerDetail?.phone || ""}`);
        }
    }

    function getSelectedCountry(editCustomerDetail: CustomerDetail | null): string {
        if (!customerDetail) return "us";
        let country = editCustomerDetail?.country || company.defaultCountryCode;
        if (country.length < 2) {
            country = "us";
        } else {
            country = country.substring(0, 2);
        }
        return country.toLowerCase();
    }

    return (
        <React.Fragment>
            <div className={"container" + (validForm ? "" : " was-validated")} key={resetId}>
                <div className="row customerInfoButtons">
                    {anyChanges && <button className="btn btn-sm btn-outline-primary col-2" onClick={saveChangesClick} disabled={!validForm}>Save Changes</button>}
                    {anyChanges && <button className="btn btn-sm btn-outline-danger col-2" onClick={cancelChangesClick}>Cancel Changes</button>}
                    {!anyChanges && <ButtonConfirm enabled={!!customer} buttonText="Reset Password" confirmTitle="Reset Password"
                        confirmMessage={`Reset password for customer ${customer.name}?`} cssClass="col-md-2" buttonSize={ButtonConfirmSize.Small}
                        onConfirm={handleResetPasswordConfirm} />}
                    {buildActivateDeactivateButtons()}
                    {buildDeleteCustomerButton()}
                    {buildBalanceDue()}
                    {getDeactivatedInfo()}
                </div>
                <div className="row pad-top-20">
                    {currentUser.isAdmin && <div className="col-md-1">
                        <label htmlFor="inputCustomerId" className="form-label">Internal Id</label>
                        <input type="number" className="form-control" id="inputCustomerId" autoComplete="off"
                            defaultValue={customerDetail?.customerId || ""} readOnly disabled />
                    </div>}
                    <div className="col-md-2">
                        <label htmlFor="inputUserId" className="form-label">User Id</label>
                        <input type="name" className={"form-control" + (validForm && validUser ? "" : " is-invalid")} id="inputUserId" autoComplete="off"
                            defaultValue={customerDetail?.userId || ""} onChange={(e) => {
                                if (!!editCustomerDetail) {
                                    editCustomerDetail.userId = e.target.value;
                                    checkHasChanges();
                                }
                            }} required={!validUser || editCustomerDetail?.userId.length === 0} />
                        {!validUser && <div className="invalid-feedback">Please choose a unique user id.</div>}
                    </div>
                    <div className="col-md-2">
                        <label htmlFor="inputEmployeeId" className="form-label">Employee Id</label>
                        <input type="name" className={"form-control" + (validForm ? "" : " is-invalid")} id="inputEmployeeId" autoComplete="off"
                            defaultValue={customerDetail?.companyEmployeeId || ""} onChange={(e) => {
                                if (!!editCustomerDetail) {
                                    editCustomerDetail.companyEmployeeId = e.target.value;
                                    checkHasChanges();
                                }
                            }} required />
                    </div>
                </div>
                <div className="row pad-top-10">
                    <div className="col-md-3">
                        <label htmlFor="inputFirstname" className="form-label">First Name</label>
                        <input type="name" className={"form-control" + (validForm ? "" : " is-invalid")} id="inputFirstname" autoComplete="off"
                            defaultValue={customerDetail?.firstName || ""} onChange={(e) => {
                                if (!!editCustomerDetail) {
                                    editCustomerDetail.firstName = e.target.value;
                                    checkHasChanges();
                                }
                            }} required />
                    </div>
                    <div className="col-md-3">
                        <label htmlFor="inputNickname" className="form-label">Nickname</label>
                        <input type="name" className="form-control" id="inputNickname" autoComplete="off"
                            defaultValue={customerDetail?.nickname || ""} onChange={(e) => {
                                if (!!editCustomerDetail) {
                                    editCustomerDetail.nickname = e.target.value;
                                    checkHasChanges();
                                }
                            }} />
                    </div>
                    <div className="col-md-3">
                        <label htmlFor="inputLastname" className="form-label">Last Name</label>
                        <input type="name" className={"form-control" + (validForm ? "" : " is-invalid")} id="inputLastname" autoComplete="off"
                            defaultValue={customerDetail?.lastName || ""} onChange={(e) => {
                                if (!!editCustomerDetail) {
                                    editCustomerDetail.lastName = e.target.value;
                                    checkHasChanges();
                                }
                            }} required />
                    </div>
                </div>
                <div className="row pad-top-10">
                    <div className="col-md-4">
                        <label htmlFor="inputEmail4" className="form-label">Email</label>
                        <div className="input-group">
                            <div className="input-group-text">@</div>
                            <input type="email" className={"form-control" + (validForm ? "" : " is-invalid")} id="inputEmail4" autoComplete="off"
                                defaultValue={customerDetail?.email || ""} onChange={(e) => {
                                    if (!!editCustomerDetail) {
                                        editCustomerDetail.email = e.target.value;
                                        checkHasChanges();
                                    }
                                }} required />
                        </div>
                    </div>
                    <div className="col-md-3">
                        <label className="form-label">Phone</label>
                        <PhoneInput name="inputPhone" defaultCountry={customerDetail?.country?.toLocaleLowerCase() || "us"}
                            value={getPhoneNumber(customerDetail)} onChange={(phone, meta) => setPhoneValues(phone, meta)} required />
                    </div></div>
                <div className="row pad-top-10">
                    <div className="col-md-4">
                        <label htmlFor="inputAddress" className="form-label">Address</label>
                        <input type="text" className="form-control" id="inputAddress" placeholder="1234 Main St" autoComplete="off"
                            defaultValue={customerDetail?.address1 || ""} onChange={(e) => {
                                if (!!editCustomerDetail) {
                                    editCustomerDetail.address1 = e.target.value;
                                    checkHasChanges();
                                }
                            }} />
                    </div>
                    <div className="col-md-4">
                        <label htmlFor="inputAddress2" className="form-label">Address 2</label>
                        <input type="text" className="form-control" id="inputAddress2" placeholder="Apartment, studio, floor, etc" autoComplete="off"
                            defaultValue={customerDetail?.address2 || ""} onChange={(e) => {
                                if (!!editCustomerDetail) {
                                    editCustomerDetail.address2 = e.target.value;
                                    checkHasChanges();
                                }
                            }} />
                    </div>
                </div>
                <div className="row pad-top-10">
                    <div className="col-md-3">
                        <label htmlFor="inputCity" className="form-label">City</label>
                        <input type="text" className="form-control" id="inputCity" autoComplete="off"
                            defaultValue={customerDetail?.city || ""} onChange={(e) => {
                                if (!!editCustomerDetail) {
                                    editCustomerDetail.city = e.target.value;
                                    checkHasChanges();
                                }
                            }} />
                    </div>
                    <div className="col-md-1">
                        <label htmlFor="inputState" className="form-label">State</label>
                        <input type="text" className="form-control" id="inputState" autoComplete="off" defaultValue={customerDetail?.state || ""}
                            onChange={(e) => {
                                if (!!editCustomerDetail) {
                                    editCustomerDetail.state = e.target.value;
                                    checkHasChanges();
                                }
                            }} />
                    </div>
                    <div className="col-md-2">
                        <label htmlFor="inputZip" className="form-label">Zip</label>
                        <input type="text" className="form-control" id="inputZip" autoComplete="off"
                            defaultValue={customerDetail?.zipcode || ""} onChange={(e) => {
                                if (!!editCustomerDetail) {
                                    editCustomerDetail.zipcode = e.target.value;
                                    checkHasChanges();
                                }
                            }} />
                    </div>
                    <div className="col-md-2" key={"custdet-cntry-" + editCustomerDetail?.country}>
                        <label className="form-label">Country</label>
                        <CountrySelector selectedCountry={getSelectedCountry(editCustomerDetail)}
                            onSelect={(country: ParsedCountry) => setCountryInfo(country)} className="customer-info-detail-country" />
                    </div>
                </div>
            </div>
            <Toaster title="Customer Info" subtitle="Update" message={toastMessage} key={"custInfdetailtoast"} />
        </React.Fragment>
    )
}
